import { useState } from "react";

import { Trash2 } from "lucide-react";

import { AlertV4, ButtonV4, ChipV4, FieldV4, ModalV4 } from "../common/ui";
import {
  pickedAdviceTexts,
  toggle,
  type AdviceGroup,
  type AdviceItem,
} from "./orderAppointmentModelV4";

// Types
type ModalAdviceTemplateV4Props = {
  items: AdviceItem[];
  groups: AdviceGroup[];
  onAddItem: (item: AdviceItem) => void;
  onRemoveItem: (key: string) => void;
  onAddGroup: (group: AdviceGroup) => void;
  onRemoveGroup: (key: string) => void;
  onConfirm: (texts: string[]) => void;
  onClose: () => void;
};

const ModalAdviceTemplateV4 = (props: ModalAdviceTemplateV4Props) => {
  const [tab, setTab] = useState<"items" | "groups">("items");
  const [query, setQuery] = useState("");
  const [newText, setNewText] = useState("");
  const [pickedItems, setPickedItems] = useState<string[]>([]);
  const [pickedGroups, setPickedGroups] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [groupForm, setGroupForm] = useState<{
    name: string;
    texts: string[];
    text: string;
  } | null>(null);
  const [groupError, setGroupError] = useState("");

  // Memo
  const search = query.trim().toLowerCase();

  const shownItems = props.items.filter((item) => item.text.toLowerCase().includes(search));

  const shownGroups = props.groups.filter(
    (group) =>
      group.name.toLowerCase().includes(search) ||
      group.texts.some((text) => text.toLowerCase().includes(search)),
  );

  const texts = pickedAdviceTexts(
    props.items.filter((item) => pickedItems.includes(item.key)).map((item) => item.text),
    props.groups
      .filter((group) => pickedGroups.includes(group.key))
      .map((group) => ({
        name: group.name,
        note_templates: group.texts.map((text) => ({ text })),
      })),
  );

  const allShownPicked =
    shownItems.length > 0 && shownItems.every((item) => pickedItems.includes(item.key));

  // Handler
  const handleToggleAll = () => {
    const keys = shownItems.map((item) => item.key);

    setPickedItems(
      allShownPicked
        ? pickedItems.filter((key) => !keys.includes(key))
        : [...new Set([...pickedItems, ...keys])],
    );
  };

  const handleAddItem = () => {
    const text = newText.trim();
    const key = `c${Date.now()}`;

    props.onAddItem({ key, text, isCustom: true });
    setPickedItems([...pickedItems, key]);
    setNewText("");
    setNotice(`เพิ่ม "${text}" เข้า Template และเลือกให้แล้ว`);
  };

  const handleAddGroupText = () => {
    const text = groupForm?.text.trim() ?? "";

    if (!groupForm || !text) {
      return;
    }

    if (!props.items.some((item) => item.text === text)) {
      props.onAddItem({ key: `c${Date.now()}`, text, isCustom: true });
    }

    setGroupForm({ ...groupForm, texts: [...new Set([...groupForm.texts, text])], text: "" });
  };

  const handleSaveGroup = () => {
    if (!groupForm) {
      return;
    }

    const name = groupForm.name.trim();

    if (!name) {
      setGroupError("ตั้งชื่อชุดก่อนบันทึก");

      return;
    }

    if (props.groups.some((group) => group.name === name)) {
      setGroupError(`มีชุดชื่อ ${name} อยู่แล้ว`);

      return;
    }

    if (groupForm.texts.length === 0) {
      setGroupError("เลือกข้อความในชุดอย่างน้อย 1 ข้อ");

      return;
    }

    const key = `g${Date.now()}`;

    props.onAddGroup({ key, name, texts: groupForm.texts, isCustom: true });
    setPickedGroups([...pickedGroups, key]);
    setGroupForm(null);
    setGroupError("");
    setNotice(`สร้างชุด "${name}" (${groupForm.texts.length} ข้อ) และเลือกให้แล้ว`);
  };

  return (
    <ModalV4 title="เลือกคำแนะนำจาก Template" width={580} onClose={props.onClose}>
      <div className="hv4-ord-ap-hint">
        เลือกได้หลายรายการ ทั้งรายข้อและแบบชุด · ข้อความที่เลือกซ้ำกันในหลายชุดจะถูกเพิ่มครั้งเดียว
      </div>
      <div className="hv4-ord-ap-tabs" role="tablist">
        {(
          [
            ["items", "รายข้อ", props.items.length],
            ["groups", "ชุดคำแนะนำ", props.groups.length],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key);
              setQuery("");
            }}
          >
            {label} <span className="hv4-ord-ap-badge">{count}</span>
          </button>
        ))}
      </div>
      <input
        className="hv4-input"
        type="search"
        aria-label="ค้นหาคำแนะนำ"
        placeholder={
          tab === "items" ? "ค้นหาคำแนะนำ เช่น งดน้ำ, Drip" : "ค้นหาชื่อชุด หรือข้อความในชุด"
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {!!notice && <div className="hv4-ord-ap-notice">{notice}</div>}

      {tab === "items" ? (
        <>
          <div className="hv4-ord-ap-newbox">
            <input
              className="hv4-input"
              aria-label="คำแนะนำใหม่"
              placeholder="พิมพ์คำแนะนำใหม่ เช่น งดกาแฟก่อนมาตรวจ"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
            />
            <ButtonV4
              className="hv4-ord-ap-blue"
              disabled={!newText.trim()}
              onClick={handleAddItem}
            >
              + เพิ่มคำแนะนำ
            </ButtonV4>
          </div>
          <div className="hv4-ord-ap-picklist">
            <label className="hv4-ord-ap-pickhead">
              <input type="checkbox" checked={allShownPicked} onChange={handleToggleAll} />{" "}
              เลือกทั้งหมด
            </label>
            {shownItems.map((item) => (
              <label
                key={item.key}
                className={`hv4-ord-ap-pick${pickedItems.includes(item.key) ? " hv4-ord-ap-pick--on" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={pickedItems.includes(item.key)}
                  onChange={() => setPickedItems(toggle(pickedItems, item.key))}
                />
                <span className="hv4-ord-ap-grow">{item.text}</span>
                {item.isCustom && (
                  <>
                    <ChipV4 size="sm" tone="purple-soft">
                      เพิ่มเอง
                    </ChipV4>
                    <button
                      type="button"
                      className="hv4-ord-ap-trash"
                      aria-label={`ลบ ${item.text}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setPickedItems(pickedItems.filter((key) => key !== item.key));
                        props.onRemoveItem(item.key);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </label>
            ))}
          </div>
        </>
      ) : (
        <div className="hv4-ord-ap-picklist">
          {groupForm ? (
            <div className="hv4-ord-ap-newbox hv4-ord-ap-groupform">
              <b>สร้างชุดคำแนะนำใหม่</b>
              <FieldV4 label="ชื่อชุด *" htmlFor="hv4_ord_ap_gname">
                <input
                  id="hv4_ord_ap_gname"
                  className="hv4-input"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                />
              </FieldV4>
              <span className="hv4-label">
                เลือกข้อความในชุด * (เลือกแล้ว {groupForm.texts.length} ข้อ)
              </span>
              <div className="hv4-ord-ap-chips">
                {[...new Set([...props.items.map((item) => item.text), ...groupForm.texts])].map(
                  (text) => (
                    <button
                      key={text}
                      type="button"
                      aria-pressed={groupForm.texts.includes(text)}
                      className="hv4-ord-ap-chip"
                      onClick={() =>
                        setGroupForm({ ...groupForm, texts: toggle(groupForm.texts, text) })
                      }
                    >
                      {groupForm.texts.includes(text) ? `✓ ${text}` : text}
                    </button>
                  ),
                )}
              </div>
              <div className="hv4-ord-ap-row">
                <input
                  className="hv4-input"
                  aria-label="ข้อความใหม่ในชุด"
                  placeholder="หรือพิมพ์ข้อความใหม่เพื่อเพิ่มเข้าชุด"
                  value={groupForm.text}
                  onChange={(e) => setGroupForm({ ...groupForm, text: e.target.value })}
                />
                <ButtonV4
                  variant="ghost"
                  disabled={!groupForm.text.trim()}
                  onClick={handleAddGroupText}
                >
                  + เพิ่มข้อความ
                </ButtonV4>
              </div>
              {!!groupError && <AlertV4 tone="danger">{groupError}</AlertV4>}
              <div className="hv4-ord-ap-modal-foot">
                <ButtonV4
                  variant="ghost"
                  onClick={() => {
                    setGroupForm(null);
                    setGroupError("");
                  }}
                >
                  ยกเลิก
                </ButtonV4>
                <ButtonV4 className="hv4-ord-ap-blue" onClick={handleSaveGroup}>
                  บันทึกชุด
                </ButtonV4>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="hv4-ord-ap-newgroup"
              onClick={() => setGroupForm({ name: "", texts: [], text: "" })}
            >
              + สร้างชุดคำแนะนำใหม่
            </button>
          )}
          {shownGroups.map((group) => (
            <label
              key={group.key}
              className={`hv4-ord-ap-group${pickedGroups.includes(group.key) ? " hv4-ord-ap-group--on" : ""}`}
            >
              <input
                type="checkbox"
                checked={pickedGroups.includes(group.key)}
                onChange={() => setPickedGroups(toggle(pickedGroups, group.key))}
              />
              <span className="hv4-ord-ap-grow">
                <b>{group.name}</b>{" "}
                <ChipV4 size="sm" tone="grey">
                  {group.texts.length} ข้อ
                </ChipV4>
                <span className="hv4-ord-ap-hint">
                  {group.texts.map((text) => `• ${text}`).join(" ")}
                </span>
              </span>
              {group.isCustom && (
                <>
                  <ChipV4 size="sm" tone="purple-soft">
                    เพิ่มเอง
                  </ChipV4>
                  <button
                    type="button"
                    className="hv4-ord-ap-trash"
                    aria-label={`ลบชุด ${group.name}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setPickedGroups(pickedGroups.filter((key) => key !== group.key));
                      props.onRemoveGroup(group.key);
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </label>
          ))}
        </div>
      )}

      <div className="hv4-ord-ap-modal-foot">
        <span className="hv4-ord-ap-hint hv4-ord-ap-grow">
          เลือกแล้ว {pickedItems.length} ข้อ · {pickedGroups.length} ชุด · จะเพิ่ม {texts.length}{" "}
          ข้อความ
        </span>
        <ButtonV4 variant="ghost" onClick={props.onClose}>
          ยกเลิก
        </ButtonV4>
        <ButtonV4
          className="hv4-ord-ap-blue"
          disabled={texts.length === 0}
          onClick={() => props.onConfirm(texts)}
        >
          {texts.length === 0 ? "เลือกคำแนะนำก่อน" : `เพิ่มคำแนะนำ (${texts.length})`}
        </ButtonV4>
      </div>
    </ModalV4>
  );
};

ModalAdviceTemplateV4.displayName = "ModalAdviceTemplateV4";

export default ModalAdviceTemplateV4;
